import { useEffect, useRef, useState } from 'react'
import type { MessageFile } from '../api/types.ts'
import { forPlayback } from '../lib/audio-session.ts'

/**
 * ПРОИГРЫВАНИЕ ГОЛОСОВОГО
 *
 * Дорожка не украшение: по ней видно, где человек говорит, а где пауза,
 * и можно ткнуть в нужное место. Без неё голосовое — это чёрный ящик,
 * который приходится слушать целиком.
 *
 * Играет всегда только одно голосовое на всё приложение: иначе при
 * пролистывании ленты они начинают накладываться друг на друга.
 */

/** Общий для всех проигрывателей: кто играет сейчас. */
let currentlyPlaying: HTMLAudioElement | null = null

const formatTime = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`

export function VoicePlayer({ file, own }: { file: MessageFile; own: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [position, setPosition] = useState(0)
  const [speed, setSpeed] = useState(1)
  /** Доиграло до конца: следующее «слушать» начнёт с перезагрузки */
  const finished = useRef(false)

  const total = file.durationSec ?? 0
  const bars = file.waveform?.length ? file.waveform : Array.from({ length: 32 }, () => 30)
  const progress = total > 0 ? position / total : 0

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTime = () => setPosition(audio.currentTime)
    const onEnd = () => {
      /**
       * Перематываем в начало сами.
       *
       * Голосовое с iPhone пишется потоком, и длительности в файле нет.
       * Браузер узнаёт её, только доиграв до конца, — и после этого
       * повторное «слушать» начинало с конца и тут же заканчивалось.
       * Второй раз голосовое молчало.
       *
       * Перемотка в начало помогла на компьютере, но не на iPhone: Safari
       * такой файл перематывать не умеет вовсе. Поэтому перед повтором
       * запись перезагружается — это начало с нуля в любом браузере,
       * а сам файл берётся из кэша, заново не скачивается.
       */
      finished.current = true
      setPlaying(false)
      setPosition(0)
      if (currentlyPlaying === audio) currentlyPlaying = null
    }

    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('ended', onEnd)
    return () => {
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('ended', onEnd)
      if (currentlyPlaying === audio) currentlyPlaying = null
    }
  }, [])

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return

    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }

    // Останавливаем чужое: два голосовых одновременно не слушает никто
    if (currentlyPlaying && currentlyPlaying !== audio) currentlyPlaying.pause()
    currentlyPlaying = audio
    // Основной динамик и без оглядки на бесшумный режим: человек сам нажал «слушать»
    forPlayback()
    if (finished.current || audio.ended) {
      finished.current = false
      audio.load()
    }
    // Перезагрузка сбрасывает скорость на обычную — задаём обе
    audio.defaultPlaybackRate = speed
    audio.playbackRate = speed
    void audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
  }

  /** Переход по нажатию на дорожку. */
  const seek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || total === 0) return
    const box = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - box.left) / box.width))
    finished.current = false
    audio.currentTime = ratio * total
    setPosition(audio.currentTime)
  }

  const cycleSpeed = () => {
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1
    setSpeed(next)
    if (audioRef.current) audioRef.current.playbackRate = next
  }

  return (
    <div className={'voice' + (own ? ' is-own' : '')}>
      <audio ref={audioRef} src={file.url} preload="metadata" />

      <button className="voice-play" onClick={toggle} aria-label={playing ? 'Пауза' : 'Слушать'}>
        {playing ? (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M7 4.5 19.5 12 7 19.5z" />
          </svg>
        )}
      </button>

      <div className="voice-body">
        <div className="voice-wave" onClick={seek} role="presentation">
          {bars.map((level, index) => (
            <span
              key={index}
              className={index / bars.length <= progress ? 'is-played' : undefined}
              style={{ height: `${Math.max(8, Math.min(100, level))}%` }}
            />
          ))}
        </div>

        <div className="voice-meta">
          <span>{formatTime(playing || position > 0 ? position : total)}</span>
          <button className="voice-speed" onClick={cycleSpeed} title="Скорость">
            {speed}×
          </button>
        </div>
      </div>
    </div>
  )
}
